import { useCallback, useEffect, useState } from 'react';
import { getCaughtBannerStatus, dismissCaughtBanner } from '../Store/apiStore';

/*
 * useCaughtBannerEligibility — decides whether the dashboard
 * "<Agent> CAUGHT X CALLS" banner (Component/CaughtBanner/CaughtBanner.js)
 * is visible right now, and with which comparison data.
 *
 * Visibility is resolved SERVER-SIDE in the agent's own timezone
 * (AgentDetails.timezone) by GET /api/agent/:agentId/caught-banner:
 *   Monday          -> mode "week"   (last completed week vs the week before)
 *   1st of a month  -> mode "month"  (last completed month vs the month before)
 *   both on one day -> mode "both"   (UI slides between the two)
 * Any other day -> show:false. A US/Canada user therefore never sees Monday's
 * banner while it is still Sunday for them.
 *
 * Dismissal is stored SERVER-SIDE (agent_settings.settings.caughtBannerDismissedDate)
 * so it survives logout, which wipes localStorage. dismiss() records the
 * qualifying day on the server; the status endpoint then returns show:false for
 * that day and starts returning show:true again on the next Monday / 1st.
 *
 * Failure is soft: any error leaves show=false, so the dashboard simply
 * renders without the banner.
 */
export default function useCaughtBannerEligibility(agentId) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // Optimistic hide the instant the user clicks close; the server keeps it
  // hidden on later loads via its stored dismissal date.
  const [dismissedLocal, setDismissedLocal] = useState(false);

  useEffect(() => {
    if (!agentId) {
      setStatus(null);
      setDismissedLocal(false);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setDismissedLocal(false);

    (async () => {
      try {
        const res = await getCaughtBannerStatus(agentId);
        if (cancelled) return;
        // Server already accounts for a stored dismissal (returns show:false),
        // so no client-side dismissal bookkeeping is needed here.
        setStatus(res || null);
      } catch {
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const dismiss = useCallback(() => {
    setDismissedLocal(true); // hide immediately
    if (agentId && status?.localDate) {
      // Fire-and-forget — a failed persist just means the banner may reappear
      // on the next load; it never blocks the close.
      dismissCaughtBanner(agentId, status.localDate).catch(() => {});
    }
  }, [agentId, status]);

  return {
    loading,
    show: !!status?.show && !dismissedLocal,
    mode: status?.mode || null,
    week: status?.week || null,
    month: status?.month || null,
    timezone: status?.timezone || null,
    // Multiplier behind "Estimated Value Saved" — server-owned so the number
    // can changee without a frontend deploy.
    avgCallValue: status?.avgCallValue ?? null,
    dismiss,
  };
}
